/**
 * Programme version history — audit trail for the clinician "why" panel.
 *
 * Every AI-approved programme mutation snapshots the whole programme
 * into `db.programme_versions` BEFORE patching the exercise. This
 * component surfaces those snapshots with a compact prior→new
 * prescription diff and a link back to the source AI proposal.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { History, Sparkles, User } from "lucide-react";

function formatPrescription(p) {
  if (!p || typeof p !== "object") return "";
  const bits = [];
  if (p.sets != null && p.reps != null) bits.push(`${p.sets}×${p.reps}`);
  else if (p.sets != null) bits.push(`${p.sets} sets`);
  else if (p.reps != null) bits.push(`${p.reps} reps`);
  if (p.weight) bits.push(`@ ${p.weight}`);
  if (p.duration_seconds) bits.push(`${p.duration_seconds}s`);
  if (p.rest_seconds) bits.push(`rest ${p.rest_seconds}s`);
  if (p.tempo) bits.push(`tempo ${p.tempo}`);
  return bits.join(" · ") || "—";
}

export default function ProgrammeVersionHistory({ patientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/programmes/versions/patient/${patientId}`);
        if (!cancelled) setItems(r.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  if (loading) return null;
  if (items.length === 0) return null;

  const visible = expanded ? items : items.slice(0, 3);

  return (
    <div className="fl-card fl-card-elevated p-6 mt-4" data-testid="programme-version-history">
      <div className="flex items-center gap-2 mb-4">
        <History size={16} className="text-[#f47a20]" />
        <div className="fl-heading text-xl">Programme version history</div>
        <span className="text-[11px] px-2 py-0.5 rounded-full ml-auto"
              style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}>
          {items.length} change{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-3">
        {visible.map((v) => {
          const ai = v.change_source === "ai_proposal_accepted";
          return (
            <div key={v.id}
                 className="p-3 rounded-md border border-white/5"
                 style={{ background: "var(--fl-bg-2)" }}
                 data-testid={`version-${v.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-xs">
                  {ai ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
                          style={{ color: "#f47a20", background: "color-mix(in oklab, #f47a20 12%, transparent)", border: "1px solid color-mix(in oklab, #f47a20 30%, transparent)" }}>
                      <Sparkles size={10} /> AI-approved
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
                          style={{ color: "#93C5FD", background: "color-mix(in oklab, #3B82F6 14%, transparent)", border: "1px solid color-mix(in oklab, #3B82F6 30%, transparent)" }}>
                      <User size={10} /> Clinician edit
                    </span>
                  )}
                  <span style={{ color: "var(--fl-muted)" }}>
                    {v.changed_by_name || "—"} · {v.created_at ? new Date(v.created_at).toLocaleString() : ""}
                  </span>
                </div>
              </div>
              {v.change_note && (
                <div className="text-xs mt-2" style={{ color: "var(--fl-text-2)" }}>
                  {v.change_note}
                </div>
              )}
              {(v.prior_prescription || v.new_prescription) && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
                  <div className="px-2 py-1 rounded"
                       style={{ background: "var(--fl-bg)", color: "var(--fl-muted-2)" }}>
                    <span className="opacity-60">Prior:</span> {formatPrescription(v.prior_prescription)}
                  </div>
                  <div className="px-2 py-1 rounded"
                       style={{ background: "color-mix(in oklab, #f47a20 8%, transparent)", color: "#f47a20" }}>
                    <span className="opacity-60">New:</span> {formatPrescription(v.new_prescription)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {items.length > 3 && (
        <button type="button"
                onClick={() => setExpanded((v) => !v)}
                className="fl-btn-ghost text-xs mt-3"
                data-testid="version-history-toggle">
          {expanded ? "Show fewer" : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}
